<template>
  <div>
    <div class="toolbar">
      <el-input v-model="keyword" clearable placeholder="账号/姓名/手机号" style="width:220px" @keyup.enter.native="load" />
      <el-button type="primary" icon="el-icon-search" @click="load">查询</el-button>
      <el-button type="primary" icon="el-icon-plus" @click="openEdit()">新增用户</el-button>
    </div>
    <el-table :data="pageList" border v-loading="loading">
      <el-table-column label="序号" width="60" align="center">
        <template slot-scope="{ $index }">{{ (page - 1) * pageSize + $index + 1 }}</template>
      </el-table-column>
      <el-table-column prop="username" label="账号" width="140" />
      <el-table-column prop="nickname" label="姓名" width="120" />
      <el-table-column prop="phone" label="手机号" width="140" />
      <el-table-column prop="roleName" label="角色" min-width="160">
        <template slot-scope="{ row }">{{ displayRoles(row) }}</template>
      </el-table-column>
      <el-table-column prop="deptId" label="部门" width="140">
        <template slot-scope="{ row }">{{ deptName(row.deptId) }}</template>
      </el-table-column>
      <el-table-column prop="enabled" label="状态" width="90">
        <template slot-scope="{ row }">
          <el-tag size="mini" :type="row.enabled===1?'success':'info'">{{ row.enabled===1?'启用':'停用' }}</el-tag>
        </template>
      </el-table-column>
      <el-table-column prop="createdAt" label="创建时间" width="170" />
      <el-table-column label="操作" width="200" fixed="right">
        <template slot-scope="{ row }">
          <el-button type="text" @click="openEdit(row)">编辑</el-button>
          <el-button type="text" @click="onResetPwd(row)">重置密码</el-button>
          <el-button type="text" style="color:#ef4444" @click="onDelete(row)">删除</el-button>
        </template>
      </el-table-column>
    </el-table>
    <list-pagination
      :total="listTotal"
      :page="page"
      :page-size="pageSize"
      @current-change="onPageChange"
      @size-change="onPageSizeChange"
    />

    <el-dialog :title="form.id?'编辑用户':'新增用户'" :visible.sync="visible" width="480px">
      <p v-if="!form.id && accountUsageTip" class="quota-tip">{{ accountUsageTip }}</p>
      <el-form :model="form" label-width="90px">
        <el-form-item label="账号" required>
          <el-input v-model="form.username" />
        </el-form-item>
        <el-form-item :label="form.id?'新密码':'密码'" :required="!form.id">
          <el-input v-model="form.password" type="password" show-password :placeholder="form.id?'不修改请留空；填写须含字母和数字':'至少6位，含字母和数字'" />
        </el-form-item>
        <el-form-item label="姓名"><el-input v-model="form.nickname" /></el-form-item>
        <el-form-item label="手机号" required><el-input v-model="form.phone" /></el-form-item>
        <el-form-item label="角色" required>
          <el-select v-model="form.roleCodes" multiple collapse-tags placeholder="可多选角色" style="width:100%">
            <el-option
              v-for="r in roleOptions"
              :key="r.roleCode"
              :label="r.roleName"
              :value="r.roleCode"
            />
          </el-select>
        </el-form-item>
        <el-form-item label="部门">
          <el-select v-model="form.deptId" clearable placeholder="可选" style="width:100%">
            <el-option v-for="d in depts" :key="d.id" :label="d.deptName" :value="d.id" />
          </el-select>
        </el-form-item>
        <el-form-item label="启用">
          <el-switch v-model="form.enabled" :active-value="1" :inactive-value="0" />
        </el-form-item>
      </el-form>
      <div slot="footer">
        <el-button @click="visible=false">取消</el-button>
        <el-button type="primary" :loading="saving" @click="save">保存</el-button>
      </div>
    </el-dialog>
  </div>
</template>

<script>
import { listUsers, saveUser, deleteUser, resetUserPassword, listRoles, listDepts } from '../../api/sys'
import { fetchMe } from '../../api/auth'
import ListPagination from '../../components/ListPagination.vue'
import listPaging from '../../mixins/listPaging'
import { PASSWORD_TIP, passwordError } from '../../utils/password'
import { accountUsageText, planNameOf } from '../../utils/plan'

export default {
  name: 'SysUser',
  components: { ListPagination },
  mixins: [listPaging],
  data() {
    return {
      keyword: '',
      list: [],
      roles: [],
      depts: [],
      roleMap: {},
      deptMap: {},
      loading: false,
      saving: false,
      visible: false,
      form: {}
    }
  },
  computed: {
    roleOptions() {
      return (this.roles || []).filter(r => r.roleCode !== 'SUPPLIER' && r.roleCode !== 'WORKER')
    },
    accountUsageTip() {
      const user = this.$store.state.user || {}
      const usage = accountUsageText(user)
      if (!usage) return ''
      const plan = planNameOf(user)
      return plan ? `当前套餐：${plan}（${usage}）` : `当前${usage}`
    }
  },
  created() {
    Promise.all([this.loadRoles(), this.loadDepts()]).then(() => this.load())
  },
  methods: {
    async refreshQuota() {
      try {
        const res = await fetchMe()
        this.$store.commit('patchUser', res.data || {})
      } catch (e) { /* ignore */ }
    },
    displayRoles(row) {
      if (row.roleName) return row.roleName
      if (Array.isArray(row.roleNames) && row.roleNames.length) return row.roleNames.join('、')
      if (Array.isArray(row.roleCodes) && row.roleCodes.length) {
        return row.roleCodes.map(c => this.roleMap[c] || c).join('、')
      }
      return this.roleMap[row.roleCode] || row.roleCode || '-'
    },
    deptName(id) {
      if (!id) return '-'
      return this.deptMap[id] || id
    },
    async loadRoles() {
      const res = await listRoles()
      this.roles = res.data || []
      const map = {}
      this.roles.forEach(r => { map[r.roleCode] = r.roleName })
      this.roleMap = map
    },
    async loadDepts() {
      const res = await listDepts()
      this.depts = res.data || []
      const map = {}
      this.depts.forEach(d => { map[d.id] = d.deptName })
      this.deptMap = map
    },
    async load() {
      this.loading = true
      try {
        const res = await listUsers({ keyword: this.keyword })
        this.list = res.data || []
        this.page = 1
      } finally {
        this.loading = false
      }
    },
    async openEdit(row) {
      const codes = row
        ? (Array.isArray(row.roleCodes) && row.roleCodes.length
          ? [...row.roleCodes]
          : (row.roleCode ? [row.roleCode] : []))
        : []
      this.form = row
        ? {
          id: row.id,
          username: row.username,
          password: '',
          nickname: row.nickname || '',
          phone: row.phone || '',
          roleCodes: codes,
          deptId: row.deptId || null,
          enabled: row.enabled == null ? 1 : row.enabled
        }
        : { id: null, username: '', password: '', nickname: '', phone: '', roleCodes: [], deptId: null, enabled: 1 }
      this.visible = true
      if (!row) await this.refreshQuota()
    },
    async save() {
      const f = this.form
      if (!f.username) return this.$message.warning('请填写账号')
      if (!f.id && !f.password) return this.$message.warning('请设置密码')
      const pwdErr = passwordError(f.password, !f.id)
      if (pwdErr) return this.$message.warning(pwdErr)
      if (!f.phone || !String(f.phone).trim()) return this.$message.warning('请填写手机号')
      if (!Array.isArray(f.roleCodes) || !f.roleCodes.length) return this.$message.warning('请选择角色')
      this.saving = true
      try {
        const payload = {
          id: f.id,
          username: f.username,
          nickname: f.nickname,
          phone: f.phone,
          roleCodes: f.roleCodes,
          deptId: f.deptId,
          enabled: f.enabled
        }
        if (f.password) payload.password = f.password
        await saveUser(payload)
        this.$message.success('已保存')
        this.visible = false
        await this.refreshQuota()
        this.load()
      } finally {
        this.saving = false
      }
    },
    onResetPwd(row) {
      this.$prompt(`请输入新密码（${PASSWORD_TIP}）`, '重置密码', {
        inputPattern: /^(?=.*[A-Za-z])(?=.*\d).{6,32}$/,
        inputErrorMessage: PASSWORD_TIP,
        inputType: 'password'
      }).then(async ({ value }) => {
        await resetUserPassword(row.id, value)
        this.$message.success('密码已重置')
      }).catch(() => {})
    },
    onDelete(row) {
      this.$confirm(`确认删除用户「${row.username}」？`, '提示', { type: 'warning' })
        .then(async () => {
          await deleteUser(row.id)
          this.$message.success('已删除')
          await this.refreshQuota()
          this.load()
        })
        .catch(() => {})
    }
  }
}
</script>

<style scoped>
.quota-tip {
  margin: 0 0 12px;
  padding: 8px 12px;
  background: #f8fafc;
  border: 1px solid #e2e8f0;
  border-radius: 6px;
  color: #475569;
  font-size: 13px;
  line-height: 1.5;
}
</style>
